import { supabase } from '@/integrations/supabase/client';
import { getDefaultTemplateRules, getDefaultSectionRules } from '@/config/templateDefaults';
import type { TemplateType, ContentType, WebsiteTemplateRule, WebsiteSectionRule } from '@/types/templateRules';

export class TemplateRulesService {
  static async getTemplateRules(
    websiteId: string, 
    templateType: TemplateType, 
    contentType: ContentType
  ): Promise<{
    templateRule: WebsiteTemplateRule;
    sectionRules: WebsiteSectionRule[];
  }> {
    const key = `${templateType}:${contentType}` as const;

    // Get default rules
    const [defaultTemplateRules, defaultSectionRules] = await Promise.all([
      getDefaultTemplateRules(),
      getDefaultSectionRules()
    ]);

    // Get template type info from database
    const { data: templateTypeInfo } = await supabase
      .from('template_types')
      .select('*')
      .eq('id', templateType)
      .single();

    // Get section types from database
    const { data: sectionTypes } = await supabase
      .from('section_types')
      .select('*');

    // Create default section rules based on available section types
    const defaultSectionRulesList = sectionTypes?.map(section => ({
      section_id: section.id,
      template_type: templateType,
      content_type: contentType,
      enabled: section.id !== 'image' && section.id !== 'quote' && section.id !== 'testimonial', // Disable problematic sections by default
      html_example: `<div class="${section.id}">${section.name} content...</div>`,
      section_notes: section.description || `Default ${section.name} section`
    })) || [];

    // Fetch custom rules from database
    const [templateRule, sectionRules] = await Promise.all([
      supabase
        .from('website_template_rules')
        .select('*')
        .eq('website_id', websiteId)
        .eq('template_type', templateType)
        .eq('content_type', contentType)
        .maybeSingle(),
      supabase
        .from('website_section_rules')
        .select('*')
        .eq('website_id', websiteId)
        .eq('template_type', templateType)
        .eq('content_type', contentType)
    ]);

    // If no custom rules exist, return defaults
    if (!templateRule.data) {
      return {
        templateRule: {
          id: 'default',
          website_id: websiteId,
          template_type: templateType,
          content_type: contentType,
          allowed_length_tags: ['ultra-short', 'short', 'medium', 'medium-long', 'long', 'very-long'],
          full_html_example: `<article class="${templateType}-${contentType}"><h1>${templateTypeInfo?.name || templateType} Title</h1><div class="content">...</div></article>`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        sectionRules: defaultSectionRulesList.map(rule => ({
          id: 'default',
          website_id: websiteId,
          ...rule,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }))
      };
    }

    return {
      templateRule: templateRule.data,
      sectionRules: sectionRules.data || defaultSectionRulesList.map(rule => ({
        id: 'default',
        website_id: websiteId,
        ...rule,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }))
    };
  }

  static async updateTemplateRules(
    websiteId: string,
    templateType: TemplateType,
    contentType: ContentType,
    templateRule: Omit<WebsiteTemplateRule, 'id' | 'website_id' | 'created_at' | 'updated_at'>,
    sectionRules: Omit<WebsiteSectionRule, 'id' | 'website_id' | 'created_at' | 'updated_at'>[]
  ) {
    // First, delete existing rules for this template type and content type
    await Promise.all([
      supabase
        .from('website_template_rules')
        .delete()
        .eq('website_id', websiteId)
        .eq('template_type', templateType)
        .eq('content_type', contentType),
      supabase
        .from('website_section_rules')
        .delete()
        .eq('website_id', websiteId)
        .eq('template_type', templateType)
        .eq('content_type', contentType)
    ]);

    // Then insert new rules
    const now = new Date().toISOString();

    // Insert template rule
    const { data: updatedTemplateRule, error: templateError } = await supabase
      .from('website_template_rules')
      .insert({
        website_id: websiteId,
        template_type: templateType,
        content_type: contentType,
        ...templateRule,
        created_at: now
      })
      .select()
      .single();

    if (templateError) {
      console.error('Error saving template rule:', templateError);
      throw templateError;
    }

    // Insert section rules
    const { data: updatedSectionRules, error: sectionError } = await supabase
      .from('website_section_rules')
      .insert(
        sectionRules.map(rule => ({
          website_id: websiteId,
          template_type: templateType,
          content_type: contentType,
          section_id: rule.section_id,
          enabled: rule.enabled,
          html_example: rule.html_example || '',
          section_notes: rule.section_notes || null,
          created_at: now
        }))
      )
      .select();

    if (sectionError) {
      console.error('Error saving section rules:', sectionError);
      throw sectionError;
    }

    return {
      templateRule: updatedTemplateRule,
      sectionRules: updatedSectionRules
    };
  }

  static async resetToDefaults(websiteId: string) {
    // Delete custom rules
    await Promise.all([
      supabase
        .from('website_template_rules')
        .delete()
        .eq('website_id', websiteId),
      supabase
        .from('website_section_rules')
        .delete()
        .eq('website_id', websiteId)
    ]);
  }
} 